import MentionExtension from "@tiptap/extension-mention";
import { ReactNodeViewRenderer, ReactRenderer } from "@tiptap/react";
import tippy, { type Instance as TippyInstance } from "tippy.js";

import VariableList from "features/templates/components/TemplateEditor/VariableList";
import { TEMPLATE_VARIABLES } from "features/templates/constants";

import Variable from "./Variable";

import type { ComponentProps } from "react";
import type { DetailedCampaign } from "../../../../../pages/campaigns/types";

const Extension = MentionExtension.extend({
  name: "variable",

  addNodeView() {
    return ReactNodeViewRenderer(Variable);
  },

  addAttributes() {
    return {
      id: {
        isRequired: true,
        default: false,
        parseHTML: (element) => element.getAttribute("data-id"),
        renderHTML: (attrs) => ({
          "data-id": attrs.id,
        }),
      },
      label: {
        isRequired: true,
        default: false,
        parseHTML: (element) => element.getAttribute("data-label"),
        renderHTML: (attrs) => ({
          "data-label": attrs.label,
        }),
      },
      fallback: {
        isRequired: true,
        default: "",
        parseHTML: (element) => element.getAttribute("data-fallback"),
        renderHTML: (attrs) => ({
          "data-fallback": attrs.fallback,
        }),
      },
      isCustomVar: {
        isRequired: true,
        default: false,
        parseHTML: (element) => {
          const attr = element.getAttribute("data-is-custom-var");

          // Handle string representation of boolean value
          return attr === "true";
        },
        renderHTML: (attrs) => ({
          "data-is-custom-var": attrs.isCustomVar,
        }),
      },
    };
  },
});

export default function VariableExtension(
  customVariables: DetailedCampaign["custom_variable_keys"],
) {
  return Extension.configure({
    deleteTriggerWithBackspace: true,
    suggestion: {
      char: `{{`,
      items: ({ query }: { query: string }) =>
        [...customVariables, ...TEMPLATE_VARIABLES].filter(({ name }) =>
          name.toLowerCase().includes(query.toLowerCase()),
        ),
      render: () => {
        let reactRenderer: ReactRenderer<
          {
            onKeyDown: (props: { event: KeyboardEvent }) => boolean | undefined;
          },
          ComponentProps<typeof VariableList>
        > = null;
        let popup: TippyInstance[] | null = null;

        return {
          onStart: (onStartProps) => {
            if (!onStartProps.clientRect) {
              return;
            }

            reactRenderer = new ReactRenderer(VariableList, {
              props: {
                ...onStartProps,
                customVariables,
              },
              editor: onStartProps.editor,
            });

            popup = tippy("#templateEditor", {
              getReferenceClientRect: onStartProps.clientRect,
              content: reactRenderer.element,
              showOnCreate: true,
              interactive: true,
              trigger: "manual",
              placement: "bottom-start",
            });
          },

          onUpdate(onUpdateProps) {
            reactRenderer?.updateProps(onUpdateProps);

            if (!onUpdateProps.clientRect) {
              return;
            }

            popup?.[0].setProps({
              getReferenceClientRect: onUpdateProps.clientRect,
            });
          },

          onKeyDown(onKeyDownProps) {
            if (onKeyDownProps.event.key === "Escape") {
              popup?.[0].hide();

              return true;
            }

            return reactRenderer?.ref?.onKeyDown(onKeyDownProps);
          },

          onExit() {
            popup?.[0].destroy();
            reactRenderer?.destroy();
          },
        };
      },
    },
  });
}
